import { autenticationInterceptor } from 'app/autentication/proxy/autentication-interceptor';
import { cashFlowInterceptor } from 'app/cashFlow/proxy/cash-flow-interceptor';
import { configurationInterceptor } from 'app/configuration/proxy/configuration-interceptor';
import { dashboardInterceptor } from 'app/Dashboard/proxy/dashboard-interceptor';
import { electronicBillingInterceptor } from 'app/electronicBilling/proxy/electronicBilling-interceptor';
import { inventoryInterceptor } from 'app/inventory/proxy/inventory-interceptor';
import { reportInterceptor } from 'app/report/proxy/report-interceptor';
import { saleInterceptor } from 'app/sale/proxy/sale-interceptor';
import { castDraft } from 'immer';
import { globalInterceptor } from 'proxy/global-interceptor';
import React, { createContext, PropsWithChildren, useCallback, useEffect, useMemo, useReducer } from 'react';
import { DispatchState } from 'shared/hooks/useDispatchState';
import { setActiveBranchOfficeId } from 'shared/utils/active-branch-office';
import { DispatchObject } from 'shared/utils/dispatch-object';
import { ErrorDelivery } from 'shared/utils/error-delivery';
import { appInterceptor } from 'shared/utils/interceptors';
import { GlobalAction } from 'store/global-actions';
import { globalContextReducer, GlobalState, initialState } from 'store/reducer';

export type GlobalDispatch<T = any> = React.Dispatch<DispatchObject<GlobalAction, T>>;

export type GlobalContextType = DispatchState<GlobalState, GlobalDispatch> & {
  setIsFetching: (status: boolean) => void;
};

export const GlobalContext = createContext({} as GlobalContextType);

type GlobalContextProviderProps = PropsWithChildren<{
  extraState?: Partial<GlobalState>;
  testDispatchApp?: GlobalDispatch;
}>;

const { FinishedFetching, StartFetching, SetError, SetModalAlert } = GlobalAction;

const interceptors = [
  appInterceptor,
  globalInterceptor,
  autenticationInterceptor,
  configurationInterceptor,
  inventoryInterceptor,
  saleInterceptor,
  cashFlowInterceptor,
  reportInterceptor,
  dashboardInterceptor,
  electronicBillingInterceptor,
];

export const onInterceptWithDispatch = (dispatch: GlobalDispatch) => (error: unknown) => {
  if (error instanceof ErrorDelivery) {
    if (error.endsFlow()) dispatch({ type: FinishedFetching });

    if (error.isErrorPage()) {
      dispatch({ type: SetError, payload: error });
    }
    if (error.isModalAlert()) {
      dispatch({ type: SetModalAlert, payload: error.getRawError() });
    }
  }
};

/**
 * Mantiene la sucursal activa (se envía en cada petición) en sincronía con
 * el estado. Se hace al despachar, no en un efecto: los efectos de las
 * páginas corren antes que los del provider y su primera petición saldría
 * con la sucursal anterior.
 */
const syncActiveBranchOffice = ({ type, payload }: DispatchObject<GlobalAction>): void => {
  if (type === GlobalAction.BranchOfficeSelected) {
    setActiveBranchOfficeId(payload?.id);
  }

  if (type === GlobalAction.ClearState || type === GlobalAction.ImpersonationStarted) {
    setActiveBranchOfficeId(null);
  }
};

export const GlobalContextProvider: React.FC<GlobalContextProviderProps> = ({
  children,
  extraState = {},
  testDispatchApp,
}: GlobalContextProviderProps) => {
  const [state, dispatchState] = useReducer(globalContextReducer, { ...initialState, ...extraState });
  const dispatch = useCallback<GlobalDispatch>((action) => {
    syncActiveBranchOffice(action);
    dispatchState(action);
  }, []);
  const setIsFetching = (status: boolean): void => dispatch({ type: status ? StartFetching : FinishedFetching });
  const value = useMemo(() => {
    return { dispatch: testDispatchApp || dispatch, state: castDraft(state), setIsFetching };
  }, [dispatch, state]);

  useEffect(() => {
    const onInterceptErrorCallback = onInterceptWithDispatch(dispatch);
    interceptors.forEach((interceptor) => {
      interceptor.reset().onIntercept(onInterceptErrorCallback);
    });
  }, [dispatch]);

  return <GlobalContext.Provider value={value}>{children}</GlobalContext.Provider>;
};
